import { ENDPIONTS, httpService } from "@api";
import { MyBarLoader } from "@shared";
import { PAYROLL_STATUS_LABELS, PayrollApprovalsVM, PayrollStatus, payrollStatusTone } from "@viewModels";
import { useCallback, useEffect, useState } from "react";
import PayrollStageTracker from "./PayrollStageTracker";
import { confirmAdvancePayroll, confirmReturnPayroll } from "./payrollWorkflowDialogs";

interface Props {
  payrollId: number | string;
  // Called after this panel approved or returned the payroll.
  onChanged?: () => void;
}

// Approval status, the user's available decisions and the history of one payroll.
const PayrollApprovalPanel = ({ payrollId, onChanged }: Props) => {
  const [data, setData] = useState<PayrollApprovalsVM | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    const res = await httpService(ENDPIONTS.payrollApprovals).getById(payrollId);
    if (res?.status === 200) setData(res.data);
    setIsLoading(false);
  }, [payrollId]);

  useEffect(() => {
    load();
  }, [load]);

  if (isLoading && !data) return <MyBarLoader />;
  if (!data) return null;

  const ref = { id: data.id, title: `${data.branch ?? ""} - ${data.forMonth}`, actions: data.actions };
  const afterChange = () => {
    load();
    onChanged?.();
  };

  const tone = payrollStatusTone(data.status);

  return (
    <div className="card approval-panel">
      <div className="card-body">
        <div className="approval-panel-head">
          <div>
            <h4 className="card-title mb-1">Approval status</h4>
            <span className={`badge badge-${tone}`}>{PAYROLL_STATUS_LABELS[data.status] ?? data.status}</span>
            {data.status !== PayrollStatus.Approved && (
              <span className="text-muted ms-2">Waiting for: {data.actions.waitingFor}</span>
            )}
          </div>
          <div className="approval-panel-actions">
            {data.actions.canAdvance && (
              <button className="btn btn-primary btn-sm" onClick={() => confirmAdvancePayroll(ref, afterChange)}>
                <i className="fa fa-check me-1" /> {data.actions.advanceLabel ?? "Approve"}
              </button>
            )}
            {(data.actions.canReturnToHR || data.actions.canReturnToFinance) && (
              <button className="btn btn-outline-danger btn-sm" onClick={() => confirmReturnPayroll(ref, afterChange)}>
                <i className="fa fa-undo me-1" /> Return
              </button>
            )}
          </div>
        </div>

        <PayrollStageTracker status={data.status} />

        <h5 className="approval-history-title">History</h5>
        <ul className="approval-history">
          <li>
            <span className="approval-dot tone-info" />
            <div>
              <strong>Initiated by HR</strong>
              <small>
                {data.initiatedBy || "—"} · {new Date(data.initiatedAt).toLocaleString()}
              </small>
            </div>
          </li>
          {data.history.map((h) => (
            <li key={h.id}>
              <span className={`approval-dot tone-${h.action.startsWith("Returned") ? "danger" : "success"}`} />
              <div>
                <strong>
                  {h.action} — {PAYROLL_STATUS_LABELS[h.toStatus] ?? h.toStatus}
                </strong>
                <small>
                  {h.by || "—"} · {new Date(h.at).toLocaleString()}
                </small>
                {h.remarks && <p className="approval-remarks">“{h.remarks}”</p>}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default PayrollApprovalPanel;
